
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { Employee, LeaveRequest, Device, AttendanceRecord, LeaveType, Holiday, Department, Location, Notification, ReportSchedule, Role } from '../types';
import { EMPLOYEES, LEAVES, DEVICES, ATTENDANCE_LOGS, HOLIDAYS, DEPARTMENTS } from '../constants';
import { locationService } from '../services';
import notificationService from '../services/notificationService';

interface AppSettings {
  autoDeductLeave: boolean;
  offlineThresholdMinutes: number;
  enableDeviceAlerts: boolean;
}

interface AppContextType {
  employees: Employee[];
  addEmployee: (employee: Employee) => void;
  updateEmployee: (id: string, updates: Partial<Employee>) => void;
  deleteEmployee: (id: string) => void;
  leaves: LeaveRequest[];
  updateLeaveStatus: (id: string, status: 'APPROVED' | 'REJECTED') => void;
  devices: Device[];
  addDevice: (device: Device) => void;
  updateDevice: (id: string, updates: Partial<Device>) => void;
  attendanceLogs: AttendanceRecord[];
  syncDevice: (id: string) => Promise<void>;
  settings: AppSettings;
  updateSettings: (updates: Partial<AppSettings>) => void;
  leaveRules: Record<LeaveType, boolean>;
  updateLeaveRule: (type: LeaveType, deducts: boolean) => void;
  holidays: Holiday[];
  addHoliday: (holiday: Holiday) => void;
  deleteHoliday: (id: string) => void;
  departments: Department[];
  addDepartment: (dept: Department) => void;
  updateDepartment: (id: string, updates: Partial<Department>) => void;
  deleteDepartment: (id: string) => void;
  locations: Location[];
  addLocation: (loc: Location) => void;
  updateLocation: (id: string, updates: Partial<Location>) => void;
  deleteLocation: (id: string) => void;
  currentUser: Employee;
  notifications: Notification[];
  notificationsLoading: boolean;
  addNotification: (notification: Omit<Notification, 'id' | 'timestamp' | 'read'>) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  refreshNotifications: () => Promise<void>;
  reportSchedules: ReportSchedule[];
  addReportSchedule: (schedule: ReportSchedule) => void;
  deleteReportSchedule: (id: string) => void;
  toggleReportScheduleStatus: (id: string) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const defaultLeaveRules: Record<LeaveType, boolean> = {
  [LeaveType.ANNUAL]: true,
  [LeaveType.SICK]: false,
  [LeaveType.UNPAID]: false,
  [LeaveType.MATERNITY]: false,
  [LeaveType.EXCUSE]: true,
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [employees, setEmployees] = useState<Employee[]>(EMPLOYEES);
  const [leaves, setLeaves] = useState<LeaveRequest[]>(LEAVES);
  const [devices, setDevices] = useState<Device[]>(DEVICES);
  const [attendanceLogs, setAttendanceLogs] = useState<AttendanceRecord[]>(ATTENDANCE_LOGS);
  const [settings, setSettings] = useState<AppSettings>({ 
    autoDeductLeave: true,
    offlineThresholdMinutes: 60,
    enableDeviceAlerts: true
  });
  const [leaveRules, setLeaveRules] = useState<Record<LeaveType, boolean>>(defaultLeaveRules);
  const [holidays, setHolidays] = useState<Holiday[]>(HOLIDAYS);
  const [departments, setDepartments] = useState<Department[]>(DEPARTMENTS);
  const [locations, setLocations] = useState<Location[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [reportSchedules, setReportSchedules] = useState<ReportSchedule[]>([
    {
      id: 'sch-1',
      title: 'Aylık Departman Puantajı',
      reportType: 'PUANTAJ',
      frequency: 'MONTHLY',
      recipients: ['e1'],
      nextRun: '2023-11-01T08:00:00Z',
      status: 'ACTIVE',
      format: 'EXCEL'
    }
  ]);
  
  const alertedDevicesRef = useRef<Set<string>>(new Set());

  // Okunmus sistem bildirimlerini localStorage'dan al
  const getReadSystemNotifIds = (): Set<string> => {
    try {
      const stored = localStorage.getItem('readSystemNotifications');
      if (stored) {
        const parsed = JSON.parse(stored);
        // 24 saat eski olanlari temizle
        const now = Date.now();
        const filtered = parsed.filter((item: { id: string; ts: number }) => now - item.ts < 24 * 60 * 60 * 1000);
        if (filtered.length !== parsed.length) {
          localStorage.setItem('readSystemNotifications', JSON.stringify(filtered));
        }
        return new Set(filtered.map((item: { id: string }) => item.id));
      }
    } catch {}
    return new Set();
  };

  const addReadSystemNotifId = (id: string) => {
    try {
      const stored = localStorage.getItem('readSystemNotifications');
      const list = stored ? JSON.parse(stored) : [];
      if (!list.some((item: { id: string }) => item.id === id)) {
        list.push({ id, ts: Date.now() });
        localStorage.setItem('readSystemNotifications', JSON.stringify(list));
      }
    } catch {}
  };

  const markAllSystemNotifsRead = () => {
    try {
      const current = notifications.filter(n => !n.read);
      const stored = localStorage.getItem('readSystemNotifications');
      const list = stored ? JSON.parse(stored) : [];
      current.forEach(n => {
        if (!list.some((item: { id: string }) => item.id === n.id)) {
          list.push({ id: n.id, ts: Date.now() });
        }
      });
      localStorage.setItem('readSystemNotifications', JSON.stringify(list));
    } catch {}
  };

  // Backend'den bildirimleri yukle
  const refreshNotifications = useCallback(async () => {
    try {
      setNotificationsLoading(true);
      const systemNotifs = await notificationService.getSystemNotifications();
      const readIds = getReadSystemNotifIds();

      const formattedNotifications: Notification[] = systemNotifs.map(n => ({
        id: String(n.id),
        title: n.title,
        message: n.message,
        type: n.type as 'INFO' | 'WARNING' | 'ERROR',
        timestamp: n.timestamp,
        read: n.isRead || readIds.has(String(n.id)),
        relatedEntityType: n.relatedEntityType,
        relatedEntityId: n.relatedEntityId
      }));

      setNotifications(formattedNotifications);
    } catch (error) {
      console.error('Bildirimler yuklenirken hata:', error);
      setNotifications([]);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Sayfa yuklendiginde ve periyodik olarak bildirimleri guncelle
  useEffect(() => {
    // Ilk yukleme
    refreshNotifications();

    // Her 60 saniyede bir guncelle
    const interval = setInterval(refreshNotifications, 60000);

    return () => clearInterval(interval);
  }, [refreshNotifications]);

  // Lokasyonlari API'den yukle
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const result = await locationService.getAll(true); // activeOnly=true
        if (result.success && result.data) {
          // API'den gelen veriyi context formatina donustur
          const formattedLocations: Location[] = result.data.map((l: any) => ({
            id: String(l.id),
            name: l.name,
            address: l.address || l.description || '',
            type: 'OFFICE' as const
          }));
          setLocations(formattedLocations);
        }
      } catch (error) {
        console.error('Lokasyonlar yuklenirken hata:', error);
      }
    };
    loadLocations();
  }, []);

  const addNotification = (n: Omit<Notification, 'id' | 'timestamp' | 'read'>) => {
    const newNotification: Notification = {
      ...n,
      id: `notif-${Date.now()}`,
      timestamp: new Date().toISOString(),
      read: false
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const markNotificationRead = async (id: string) => {
    // Numeric ID ise backend'e de gonder
    const numericId = parseInt(id, 10);
    if (!isNaN(numericId)) {
      try {
        await notificationService.markAsRead(numericId);
      } catch (error) {
        console.error('Bildirim okundu isaretlenirken hata:', error);
      }
    }
    // Sistem bildirimlerini localStorage'a kaydet
    addReadSystemNotifId(id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
  };

  const markAllNotificationsRead = async () => {
    try {
      await notificationService.markAllAsRead();
    } catch (error) {
      console.error('Bildirimler okundu isaretlenirken hata:', error);
    }
    // Tum sistem bildirimlerini localStorage'a kaydet
    markAllSystemNotifsRead();
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const addReportSchedule = (schedule: ReportSchedule) => {
    setReportSchedules(prev => [...prev, schedule]);
    addNotification({
      title: 'Zamanlanmış Görev Eklendi',
      message: `"${schedule.title}" raporu ${schedule.frequency} periyoduyla hazırlandı.`,
      type: 'INFO'
    });
  };

  const deleteReportSchedule = (id: string) => {
    setReportSchedules(prev => prev.filter(s => s.id !== id));
  };

  const toggleReportScheduleStatus = (id: string) => {
    setReportSchedules(prev => prev.map(s => 
      s.id === id ? { ...s, status: s.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' } : s
    ));
  };

  // ONCELIK: storedAuthUser localStorage sync okuma — anlik cevap
  const storedAuthUser = (() => {
    try {
      const userStr = localStorage.getItem('pdks_user');
      return userStr ? JSON.parse(userStr) : null;
    } catch { return null; }
  })();

  // Gercek kullanicinin rol bilgisini auth'dan al.
  // ONEMLI: employees state async yuklendigi icin mount sirasinda [] olabilir.
  // baseUser = undefined olursa baseUser.role crash — currentUser.role toggle olur
  // → UI'da 'kalem edit' butonu gorunup kaybolur bug'ina sebep (ARS Ahsap raporu).
  // Guvenli default nesne ile bu race condition'ini engelle.
  const baseUser = employees.find(e => e.id === 'e1')
    || employees[0]
    || ({ id: 'e-fallback', name: storedAuthUser?.fullName ?? 'Kullanici', role: Role.MANAGER } as any);

  // Auth'dan gelen role'u Role enum'a esle
  const resolveRole = (): Role => {
    // SystemUsers.UserID=1 DAIMA admin — session Roles bosalsa veya rol atamasi
    // yanlis yapilmis olsa bile ilk kullanici (kurulum admin'i) yetkiyi kaybetmez.
    // Backend'de TimesheetApiController'da ayni kural var; frontend de hizali olmali
    // ki UI butonlari (kalem edit, admin-only sayfalar) tutarli davransin.
    const uid = storedAuthUser?.UserID ?? storedAuthUser?.userID ?? storedAuthUser?.id ?? storedAuthUser?.Id;
    if (uid === 1 || uid === '1') return Role.ADMIN;

    const roles: string[] = storedAuthUser?.roles || [];
    // console.log'lar production'da spam yapiyordu — kaldirildi

    const adminKeywords = ['admin', 'sistem yöneticisi', 'sistem yonetici', 'sistem yöneticisi', 'administrator'];
    const nfcKeywords = ['nfc_operator', 'nfc operator', 'nfc', 'nfc_sef', 'sef'];
    const deptMgrKeywords = ['department_manager', 'departmentmanager', 'departman yöneticisi', 'departman yetkilisi', 'birim yöneticisi', 'birim yetkilisi', 'dept_manager', 'deptmanager', 'hr'];
    const mgrKeywords = ['manager', 'yönetici', 'mudur', 'müdür'];

    const hasRole = (keywords: string[]) => roles.some(r => {
      const lower = r.toLowerCase().trim();
      return keywords.some(k => lower === k || lower.includes(k));
    });

    if (hasRole(adminKeywords)) return Role.ADMIN;
    if (hasRole(nfcKeywords)) return Role.NFC_OPERATOR;
    if (hasRole(deptMgrKeywords)) return Role.DEPARTMENT_MANAGER;
    if (hasRole(mgrKeywords)) return Role.MANAGER;

    // 2026-08-13 KALDIRILDI: "uid <= 3 ise ADMIN" heuristigi guvenlik acigiydi.
    // Baglar'da UserID=2 olan 'test' kullanicisi (rol: Personel) ADMIN gorunup
    // giris/cikis duzenleyebiliyordu. Artik sadece UserID=1 (kurulum admin'i) ve
    // gercek rol adi eslesmesi gecerli.

    // Rol adi hic eslesmediyse en dusuk yetki — USER (personel).
    // Onceden MANAGER donuyordu; bu da yetkisiz kullaniciya fazla yetki veriyordu.
    return Role.USER;
  };

  const currentUser = { ...baseUser, role: resolveRole() };

  const addEmployee = (employee: Employee) => {
    setEmployees(prev => [...prev, employee]);
  };

  const updateEmployee = (id: string, updates: Partial<Employee>) => {
    setEmployees(prev => prev.map(emp => emp.id === id ? { ...emp, ...updates } : emp));
  };

  const deleteEmployee = (id: string) => {
    setEmployees(prev => prev.filter(emp => emp.id !== id));
  };

  const updateLeaveStatus = (id: string, status: 'APPROVED' | 'REJECTED') => {
    setLeaves(prev => prev.map(leave => leave.id === id ? { ...leave, status } : leave));
  };

  const updateLeaveRule = (type: LeaveType, deducts: boolean) => {
    setLeaveRules(prev => ({ ...prev, [type]: deducts }));
  };

  const addHoliday = (holiday: Holiday) => {
    setHolidays(prev => [...prev, holiday]);
  };

  const deleteHoliday = (id: string) => {
    setHolidays(prev => prev.filter(h => h.id !== id));
  };

  const addDepartment = (dept: Department) => {
    setDepartments(prev => [...prev, dept]);
  };

  const updateDepartment = (id: string, updates: Partial<Department>) => {
    setDepartments(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const deleteDepartment = (id: string) => {
    setDepartments(prev => prev.filter(d => d.id !== id));
  };

  const addLocation = (loc: Location) => {
    setLocations(prev => [...prev, loc]);
  };

  const updateLocation = (id: string, updates: Partial<Location>) => {
    setLocations(prev => prev.map(l => l.id === id ? { ...l, ...updates } : l));
  };

  const deleteLocation = (id: string) => {
    setLocations(prev => prev.filter(l => l.id !== id));
  };

  const addDevice = (device: Device) => {
    setDevices(prev => [...prev, device]);
  };

  const updateDevice = (id: string, updates: Partial<Device>) => {
    setDevices(prev => prev.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const syncDevice = async (id: string) => {
    await new Promise(resolve => setTimeout(resolve, 2000));
    const now = new Date().toISOString();
    alertedDevicesRef.current.delete(id);
    setDevices(prev => prev.map(d => d.id === id ? { ...d, lastSync: now, status: 'ONLINE' } : d));
    const newLogs: AttendanceRecord[] = Array.from({ length: 3 }).map((_, i) => {
      const randomEmp = employees[Math.floor(Math.random() * employees.length)];
      return {
        id: `sync-${Date.now()}-${i}`,
        employeeId: randomEmp.id,
        deviceId: id,
        timestamp: new Date().toISOString(),
        type: Math.random() > 0.5 ? 'ENTRY' : 'EXIT'
      };
    });
    setAttendanceLogs(prev => [...newLogs, ...prev]);
  };

  const updateSettings = (updates: Partial<AppSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  };

  return (
    <AppContext.Provider value={{ 
      employees, 
      addEmployee, 
      updateEmployee, 
      deleteEmployee, 
      leaves, 
      updateLeaveStatus, 
      devices,
      addDevice,
      updateDevice,
      attendanceLogs,
      syncDevice,
      settings, 
      updateSettings,
      leaveRules,
      updateLeaveRule,
      holidays,
      addHoliday,
      deleteHoliday,
      departments,
      addDepartment,
      updateDepartment,
      deleteDepartment,
      locations,
      addLocation,
      updateLocation,
      deleteLocation,
      currentUser,
      notifications,
      notificationsLoading,
      addNotification,
      markNotificationRead,
      markAllNotificationsRead,
      refreshNotifications,
      reportSchedules,
      addReportSchedule,
      deleteReportSchedule,
      toggleReportScheduleStatus
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within AppProvider');
  return context;
};
